import { toast } from "sonner";

import { areGeometriesEqual, computeValue } from "../measurement-overlay-helpers";
import type {
  MeasurementGeometry,
  MeasurementOverlayRecord,
  PolygonGeometry,
} from "../measurement-overlay-types";
import type { MeasurementScale } from "@/hooks/useMeasurementScale";

type PreviewGeometryMap = Record<string, MeasurementGeometry | PolygonGeometry>;

interface CommitPreviewMeasurementUpdatesArgs {
  previewGeometries: PreviewGeometryMap;
  measurements: MeasurementOverlayRecord[];
  scale: MeasurementScale | null;
  onUpdateMeasurement: (
    measurementId: MeasurementOverlayRecord["_id"],
    geometry: MeasurementGeometry | PolygonGeometry,
    height: number | undefined,
    value: number
  ) => Promise<void>;
}

export async function commitPreviewMeasurementUpdates({
  previewGeometries,
  measurements,
  scale,
  onUpdateMeasurement,
}: CommitPreviewMeasurementUpdatesArgs): Promise<boolean> {
  const pendingUpdates = Object.entries(previewGeometries)
    .map(([measurementId, geometry]) => {
      const measurement = measurements.find((item) => String(item._id) === measurementId);
      if (!measurement || areGeometriesEqual(geometry, measurement.geometry)) {
        return null;
      }

      const nextValue = scale
        ? computeValue(
            measurement.tool,
            measurement.measurementType,
            geometry,
            measurement.height,
            scale.pixelsPerFoot,
            scale.pageAspectRatio
          )
        : measurement.value;
      return { measurement, geometry, nextValue };
    })
    .filter((update): update is {
      measurement: MeasurementOverlayRecord;
      geometry: MeasurementGeometry | PolygonGeometry;
      nextValue: number;
    } => !!update);

  if (pendingUpdates.length === 0) {
    return true;
  }

  try {
    await Promise.all(
      pendingUpdates.map((update) =>
        onUpdateMeasurement(
          update.measurement._id,
          update.geometry,
          update.measurement.height,
          update.nextValue
        )
      )
    );
    return true;
  } catch (error) {
    console.error("Measurement update failed:", error);
    toast.error("Failed to update measurement");
    return false;
  }
}
